
/**
 * Module dependencies.
 */

import { Note as NoteProps, ProductProps } from 'src/types/products';
import { color, media, units } from 'src/styles/utils';
import { ifProp } from 'styled-tools';
import { normalizeNewLine } from 'src/core/utils/strings';
import { useTranslate } from 'src/core/utils/translator';
import Container from 'src/components/core/layout/container';
import FadeInUp from 'src/components/core/animations/fade-in-up';
import Parallax from 'src/components/core/animations/parallax';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import useProductSpecs from 'src/hooks/use-product-specs';

/**
 * `SpecProps` type.
 */

type SpecProps = {
  active: boolean,
  icon: string,
  name?: string,
  uppercase?: boolean,
  value: string
};

/**
 * `Props` type.
 */

type Props = ProductProps & {
  isKiosk?: boolean
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  background-color: ${color('beige100')};
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title     title    .'
      '. specs     specs    .'
      '. noteTitle noteTitle .'
      '. notes     .        .';
    grid-template-columns: 1fr 6fr 4fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    grid-area: title;
    margin-bottom: ${units(13)};
  `}
`;

/**
 * `SpecsGridWrapper` styled component.
 */

const SpecsGridWrapper = styled.div`
  grid-area: specs;
`;

/**
 * `SpecsGrid` styled component.
 */

const SpecsGrid = styled.div<{ hasNotes: boolean }>`
  display: grid;
  grid-auto-rows: 1fr;
  grid-gap: 1px;
  grid-template-columns: repeat(2, 1fr);

  ${media.min('ms')`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('md')`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(5, 1fr);
  `}

  ${ifProp('hasNotes', css`
    margin-bottom: ${units(13)};

    ${media.min('ms')`
      margin-bottom: ${units(25)};
    `}
  `)}
`;

/**
 * `Spec` styled component.
 */

const Spec = styled(FadeInUp)`
  align-items: center;
  background-color: ${color('beige50')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${units(8.5)} ${units(2)} ${units(2)} ${units(2)};

  ${media.min('ms')`
    padding: ${units(8.5)} ${units(2)} 34px ${units(2)};
  `}
`;

/**
 * `SpecIcon` styled component.
 */

const SpecIcon = styled(Svg).attrs({ size: units(9) })`
  margin-bottom: ${units(3.5)};
`;

/**
 * `SpecName` styled component.
 */

const SpecName = styled(Type.Small).attrs({ as: 'span' })`
  color: ${color('grey300')};
  letter-spacing: 0.5px;
  text-align: center;
  text-transform: uppercase;
`;

/**
 * `SpecValue` styled component.
 */

const SpecValue = styled(Type.Small).attrs({ as: 'span' })<{
  uppercase?: boolean
}>`
  letter-spacing: 0.5px;
  text-align: center;

  ${ifProp('uppercase', css`
    text-transform: uppercase;
  `)}
`;

/**
 * `NoteTitle` styled component.
 */

const NoteTitle = styled(Type.H5)`
  grid-area: noteTitle;
  margin-bottom: ${units(3)};

  ${media.min('ms')`
    margin-bottom: ${units(5)};
  `}
`;

/**
 * `Notes` styled component.
 */

const Notes = styled.div`
  grid-area: notes;
`;

/**
 * `Note` styled component.
 */

const Note = styled(Type.Paragraph)`
  color: ${color('grey400')};

  &:not(:last-child) {
    margin-bottom: ${units(3)};
  }

  ${media.min('ms')`
    &:not(:last-child) {
      margin-bottom: ${units(4)};
    }
  `}
`;

/**
 * `SpecsSection` component.
 */

const SpecsSection = (props: Props): ReactElement => {
  const { translate } = useTranslate();
  const { isKiosk, notes } = props;
  const { normalizedProductSpecs } = useProductSpecs(props);
  const hasNotes = !isEmpty(notes);

  return (
    <Section>
      <Container hasPadding={!isKiosk}>
        <Grid>
          <Title>
            <ParallaxFadeInUp parallaxOptions={{ percentage: 0.1 }}>
              {translate('products:labels.characteristics')}
            </ParallaxFadeInUp>
          </Title>

          <SpecsGridWrapper>
            <Parallax speed={2}>
              <SpecsGrid hasNotes={hasNotes}>
                {normalizedProductSpecs && normalizedProductSpecs.map((specs: SpecProps, index: number) => {
                  const { active, icon, name, uppercase, value } = specs;

                  if (!active) {
                    return null;
                  }

                  return (
                    <Spec key={index}>
                      <SpecIcon icon={icon} />

                      {name && (
                        <SpecName>
                          {name}
                        </SpecName>
                      )}

                      <SpecValue uppercase={uppercase}>
                        <RawHtml>
                          {value}
                        </RawHtml>
                      </SpecValue>
                    </Spec>
                  );
                })}
              </SpecsGrid>
            </Parallax>
          </SpecsGridWrapper>

          {hasNotes && (
            <>
              <NoteTitle>
                <ParallaxFadeInUp parallaxOptions={{ percentage: 0.1 }}>
                  {translate('common:labels.notes')}
                </ParallaxFadeInUp>
              </NoteTitle>

              <Notes>
                <ParallaxFadeInUp
                  parallaxOptions={{
                    percentage: 0.1,
                    speed: 1 }}
                >
                  {notes.map(({ description, id }: NoteProps) => (
                    <RawHtml
                      element={Note}
                      key={id}
                    >
                      {normalizeNewLine(description)}
                    </RawHtml>
                  ))}
                </ParallaxFadeInUp>
              </Notes>
            </>
          )}
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `SpecsSection` component.
 */

export default SpecsSection;
